
import { Component, Vue, Watch } from 'vue-property-decorator'
import axios from 'axios'
import { notification } from 'ant-design-vue'
import { ApexOptions } from 'apexcharts'
import moment, { Moment } from 'moment/moment'
import PageHeader from '@/components/base/PageHeader.vue'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import Column, { FilterOption } from '@/types/column'
import Pagination from '@/types/pagination'
import TableSettings from '@/types/tableSettings'
import ls from '@/ls'

type CountItem = { name: string, count: number }
type FilterOptionItem = { id: number|string, name: string }
type PeriodBounds = { min?: string|null, max?: string|null }
type KAMIncidentOverviewRow = {
  id: number
  entity_id?: number
  entity: string
  project_id?: number
  project_reference: string
  project_name: string
  construction_type_ids: number[]
  construction_types: string[]
  site_manager_ids: number[]
  site_managers: string[]
  project_leader_ids: number[]
  project_leaders: string[]
  incident_type_ids: string[]
  incident_types: string[]
  sickness_absence: boolean
  form_reference?: string
  form_name?: string
  status: number
  status_label: string
  created_at?: string
}
type KAMIncidentOverviewList = {
  data: KAMIncidentOverviewRow[]
  meta: {
    current_page: number
    per_page: number
    total: number
    last_page: number
  }
  filters: Record<string, FilterOptionItem[]|PeriodBounds>
  counts: {
    total: number
    sickness_absence: number
    by_entity: CountItem[]
    by_construction_type: CountItem[]
    by_incident_type: CountItem[]
  }
}

@Component({
  components: { PageHeader, ActiveFilters }
})
export default class KAMIncidentOverview extends Vue {
  localStorageName = 'KAMIncidentOverviewTableSettings'
  loading = false
  exporting = false

  overview: KAMIncidentOverviewList = {
    data: [],
    meta: {
      current_page: 1,
      per_page: 25,
      total: 0,
      last_page: 1
    },
    filters: {},
    counts: {
      total: 0,
      sickness_absence: 0,
      by_entity: [],
      by_construction_type: [],
      by_incident_type: []
    }
  }

  columns: Column[] = [
    { title: 'Entiteit', key: 'entity_id', dataIndex: 'entity', filters: [], sorter: false, width: 180 },
    { title: 'Projectnummer', key: 'project_reference', dataIndex: 'project_reference', filters: [], sorter: false, width: 150 },
    { title: 'Projectnaam', key: 'project_id', dataIndex: 'project_name', filters: [], sorter: false, width: 240 },
    { title: 'Projecttype (bouwsoort)', key: 'construction_type_id', dataIndex: 'construction_types', scopedSlots: { customRender: 'arrayValue' }, filters: [], sorter: false, width: 220 },
    { title: 'Uitvoerder(s)', key: 'site_manager_id', dataIndex: 'site_managers', scopedSlots: { customRender: 'arrayValue' }, filters: [], sorter: false, width: 220 },
    { title: 'Projectleider(s)', key: 'project_leader_id', dataIndex: 'project_leaders', scopedSlots: { customRender: 'arrayValue' }, filters: [], sorter: false, width: 220 },
    { title: 'Soort incident(en)', key: 'incident_type', dataIndex: 'incident_types', scopedSlots: { customRender: 'arrayValue' }, filters: [], sorter: false, width: 220 },
    { title: 'Gemeld op', key: 'created_at', dataIndex: 'created_at', scopedSlots: { customRender: 'created_at' }, sorter: true, sortDirections: ['ascend', 'descend'], width: 170 },
    { title: 'Verzuim', key: 'sickness_absence', dataIndex: 'sickness_absence', scopedSlots: { customRender: 'sickness_absence' }, filters: [{ text: 'Ja', value: 1 }, { text: 'Nee', value: 0 }], sorter: false, width: 130, align: 'center' }
  ]

  tableSettings: TableSettings = ls.get(this.localStorageName) || {
    pagination: { current: 1, pageSize: 25 },
    showSizeChanger: true,
    pageSizeOptions: ['25', '50', '100', '250'],
    activeFilters: {},
    sort: { columnKey: 'created_at', order: 'descend' },
    search: '',
    archived: false,
    createdAtPeriod: null
  }

  mounted (): void {
    if (typeof this.tableSettings.createdAtPeriod === 'undefined') this.$set(this.tableSettings, 'createdAtPeriod', null)
    this.reload()
    this.onTableSettingsChange()
  }

  async reload (): Promise<void> {
    this.loading = true
    try {
      const response = await axios.get(`${process.env.VUE_APP_API_URL}/kamForms/incidents/overview`, {
        params: this.requestParams()
      })
      this.overview = response.data
      this.onFiltersChange()
    } catch {
      notification.error({ message: 'Fout tijdens het ophalen van de KAM-incidenten!', description: 'Er is iets mis gegaan. Probeer het later nog een keer.', duration: 3 })
    } finally {
      this.loading = false
    }
  }

  requestParams (): URLSearchParams {
    const params = new URLSearchParams()
    params.append('limit', String(this.tableSettings.pagination.pageSize))
    params.append('page', String(this.tableSettings.pagination.current))
    if (this.tableSettings.sort?.columnKey) {
      const prefix = this.tableSettings.sort.order === 'descend' ? '-' : ''
      params.append('sort', prefix + this.tableSettings.sort.columnKey)
    }

    Object.entries(this.tableSettings.activeFilters || {}).forEach(([key, values]) => {
      if (values && values.length) params.append(`filter[${key}]`, values.join(','))
    })

    if (this.tableSettings.createdAtPeriod?.from) {
      params.append('filter[created_at_from]', this.tableSettings.createdAtPeriod.from)
    }

    if (this.tableSettings.createdAtPeriod?.to) {
      params.append('filter[created_at_to]', this.tableSettings.createdAtPeriod.to)
    }

    return params
  }

  resetPeriodFilter (): void {
    this.$set(this.tableSettings, 'createdAtPeriod', null)
    this.tableSettings.pagination.current = 1
    this.reload()
  }

  handlePeriodFromChange (date: string|null): void {
    this.updatePeriodFilter(date, this.selectedPeriodTo)
  }

  handlePeriodToChange (date: string|null): void {
    this.updatePeriodFilter(this.selectedPeriodFrom, date)
  }

  updatePeriodFilter (from: string|null, to: string|null): void {
    if (from && to && this.isAfter(from, to)) return

    this.$set(this.tableSettings, 'createdAtPeriod', (!from && !to) || this.isFullPeriodRange(from, to) ? null : { from, to })
    this.tableSettings.pagination.current = 1
    this.reload()
  }

  handleTableChange (pagination:{ current:number, pageSize:number }, filters:Record<string, (number|string)[]>, sort:{ columnKey:string, order:string }): void {
    Object.keys(filters || {}).forEach((key) => {
      if (!filters[key] || !filters[key].length) delete filters[key]
    })

    this.tableSettings.pagination = pagination
    this.tableSettings.activeFilters = filters
    this.tableSettings.sort = sort.order ? sort : null
    this.reload()
  }

  updateFilters (filters: Record<string, (string|number|boolean)[]>): void {
    this.columns.forEach((column: Column) => {
      column.filteredValue = column.key in filters ? filters[column.key] : []
    })
    this.tableSettings.pagination.current = 1
    this.tableSettings.activeFilters = Object.assign({}, filters)
    this.reload()
  }

  async exportOverview (): Promise<void> {
    this.exporting = true
    try {
      const response = await axios.post(`${process.env.VUE_APP_API_URL}/kamForms/incidents/overview/export`, {
        filter: this.exportFilters(),
        sort: this.tableSettings.sort?.columnKey ? `${this.tableSettings.sort.order === 'descend' ? '-' : ''}${this.tableSettings.sort.columnKey}` : '-created_at'
      }, { responseType: 'arraybuffer' })
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'Export_KAM_incidenten.xlsx'
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      notification.success({ message: 'Export gestart.', description: 'De Excel export is gestart. Check je downloadmap.', duration: 3 })
    } catch {
      notification.error({ message: 'Export mislukt.', description: 'Het exporteren van KAM-incidenten is mislukt.', duration: 0 })
    } finally {
      this.exporting = false
    }
  }

  exportFilters (): Record<string, (string|number|boolean)[]|string> {
    const filters: Record<string, (string|number|boolean)[]|string> = { ...(this.tableSettings.activeFilters || {}) }
    if (this.tableSettings.createdAtPeriod?.from) filters.created_at_from = this.tableSettings.createdAtPeriod.from
    if (this.tableSettings.createdAtPeriod?.to) filters.created_at_to = this.tableSettings.createdAtPeriod.to
    return filters
  }

  onRowClick (item: KAMIncidentOverviewRow): { on: { click: () => void }} {
    return {
      on: {
        click: () => this.navigateTo(item)
      }
    }
  }

  navigateTo (item: KAMIncidentOverviewRow): void {
    const params: Record<string, string> = { kamFormId: item.id.toString() }
    if (item.form_reference) params.formReference = item.form_reference
    this.$router.push({ name: 'kamFormEdit', params })
  }

  formatList (items: string[] | null): string {
    return items && items.length ? items.join(', ') : '-'
  }

  chartOptions (labels: string[], filterKey: string): ApexOptions {
    return {
      chart: {
        toolbar: { show: false },
        sparkline: { enabled: false },
        events: {
          dataPointSelection: (event: MouseEvent, chartContext: unknown, config: { dataPointIndex?: number }) => {
            this.applyChartFilter(filterKey, labels[config.dataPointIndex ?? -1])
          }
        }
      },
      dataLabels: { enabled: false },
      grid: { strokeDashArray: 3 },
      xaxis: { categories: labels, labels: { rotate: -35, trim: true } },
      yaxis: { decimalsInFloat: 0, forceNiceScale: true },
      colors: ['#f38929'],
      plotOptions: { bar: { borderRadius: 2, columnWidth: '45%' } },
      states: { active: { filter: { type: 'darken', value: 0.75 } } },
      tooltip: { y: { formatter: (value: number) => String(value) } }
    }
  }

  applyChartFilter (filterKey: string, label?: string): void {
    if (!label || label === '-') return

    const value = this.filterValueForLabel(filterKey, label)
    if (value === null) return

    const filters = Object.assign({}, this.tableSettings.activeFilters || {})
    const current = filters[filterKey] || []
    const isActive = current.length === 1 && String(current[0]) === String(value)

    if (isActive) {
      delete filters[filterKey]
    } else {
      filters[filterKey] = [value]
    }

    this.updateFilters(filters)
  }

  filterValueForLabel (filterKey: string, label: string): string|number|null {
    const filterMap: Record<string, string> = {
      entity_id: 'entities',
      construction_type_id: 'construction_types',
      incident_type: 'incident_types'
    }
    const options = this.overview.filters[filterMap[filterKey]]

    if (!Array.isArray(options)) return null

    const option = options.find(item => item.name === label)
    return option?.id ?? null
  }

  chartSeries (items: CountItem[]): Array<{ name: string, data: number[] }> {
    return [{ name: 'Aantal', data: items.map((item) => item.count) }]
  }

  get counts (): KAMIncidentOverviewList['counts'] {
    return this.overview.counts
  }

  get periodBounds (): { min: string|null, max: string|null } {
    const period = this.overview.filters.period as PeriodBounds|undefined
    if (period?.min && period?.max) {
      return {
        min: period.min,
        max: period.max
      }
    }

    const dates = (this.overview.data || [])
      .map(item => item.created_at ? item.created_at.slice(0, 10) : null)
      .filter((date): date is string => Boolean(date))
      .sort()

    if (!dates.length) return { min: null, max: null }

    return {
      min: dates[0],
      max: dates[dates.length - 1]
    }
  }

  get hasActivePeriodFilter (): boolean {
    return Boolean(this.tableSettings.createdAtPeriod?.from || this.tableSettings.createdAtPeriod?.to)
  }

  get selectedPeriodFrom (): string|null {
    return this.tableSettings.createdAtPeriod?.from || this.periodBounds.min
  }

  get selectedPeriodTo (): string|null {
    return this.tableSettings.createdAtPeriod?.to || this.periodBounds.max
  }

  get incidentChartOptions (): ApexOptions {
    return this.chartOptions(this.counts.by_incident_type.map((item) => item.name), 'incident_type')
  }

  get incidentChartSeries (): Array<{ name: string, data: number[] }> {
    return this.chartSeries(this.counts.by_incident_type)
  }

  get entityChartOptions (): ApexOptions {
    return this.chartOptions(this.counts.by_entity.map((item) => item.name), 'entity_id')
  }

  get entityChartSeries (): Array<{ name: string, data: number[] }> {
    return this.chartSeries(this.counts.by_entity)
  }

  get constructionTypeChartOptions (): ApexOptions {
    return this.chartOptions(this.counts.by_construction_type.map((item) => item.name), 'construction_type_id')
  }

  get constructionTypeChartSeries (): Array<{ name: string, data: number[] }> {
    return this.chartSeries(this.counts.by_construction_type)
  }

  get pagination (): Pagination {
    return {
      current: this.tableSettings.pagination.current,
      defaultPageSize: this.tableSettings.pagination.pageSize,
      pageSize: Number(this.overview.meta.per_page),
      total: this.overview.meta.total,
      showTotal: (total, range) => `KAM-incident ${range[0]} t/m ${range[1]} van de ${total}`,
      showSizeChanger: this.tableSettings.showSizeChanger,
      pageSizeOptions: this.tableSettings.pageSizeOptions
    }
  }

  @Watch('overview.filters', { immediate: true, deep: true })
  onFiltersChange (): void {
    const map: Record<string, string> = {
      entity_id: 'entities',
      project_reference: 'project_references',
      project_id: 'project_names',
      construction_type_id: 'construction_types',
      site_manager_id: 'site_managers',
      project_leader_id: 'project_leaders',
      incident_type: 'incident_types'
    }

    this.columns.forEach((column) => {
      if (column.key === 'sickness_absence') return

      const filterKey = map[column.key]
      const options = filterKey ? this.overview.filters[filterKey] : null
      if (Array.isArray(options)) {
        column.filters = options.map((item) => ({ text: item.name, value: item.id })) as FilterOption[]
      }
    })
  }

  @Watch('tableSettings', { deep: true })
  onTableSettingsChange (): void {
    ls.set(this.localStorageName, this.tableSettings)
    axios.post(process.env.VUE_APP_API_URL + '/users/admin/settings', {
      settingName: this.localStorageName,
      settingValue: this.tableSettings
    })

    this.columns.forEach((column: Column) => {
      column.filteredValue = this.tableSettings.activeFilters && column.key in this.tableSettings.activeFilters ? this.tableSettings.activeFilters[column.key] : null
      if (this.tableSettings.sort && column.key === this.tableSettings.sort.columnKey) {
        column.defaultSortOrder = this.tableSettings.sort.order
        column.sortOrder = this.tableSettings.sort.order
      } else {
        column.sortOrder = false
      }
    })
  }

  disabledPeriodFromDate (current: Moment): boolean {
    if (!current || !this.periodBounds.min || !this.periodBounds.max) return true

    return current.isBefore(this.dateMoment(this.periodBounds.min), 'day') || current.isAfter(this.dateMoment(this.selectedPeriodTo || this.periodBounds.max), 'day')
  }

  disabledPeriodToDate (current: Moment): boolean {
    if (!current || !this.periodBounds.min || !this.periodBounds.max) return true

    return current.isBefore(this.dateMoment(this.selectedPeriodFrom || this.periodBounds.min), 'day') || current.isAfter(this.dateMoment(this.periodBounds.max), 'day')
  }

  dateMoment (date: string): Moment {
    return moment(date, 'YYYY-MM-DD')
  }

  isAfter (from: string, to: string): boolean {
    return this.dateMoment(from).isAfter(this.dateMoment(to), 'day')
  }

  isFullPeriodRange (from: string|null, to: string|null): boolean {
    return from === this.periodBounds.min && to === this.periodBounds.max
  }
}
